import React from "react";
import { parseBooleanVal } from "Utils/utils";
import { STATE_FULL_LABEL_OPTIONS } from "Constants/constant";
import { FormReadOnlyTable } from "./FormReadOnlyField";

const formatFieldLabel = (label) => {
  if (!label) return "";
  return String(label)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const FieldHint = ({ hint }) => {
  if (!hint) return null;
  return (
    <div
      className="form-field-hint"
      dangerouslySetInnerHTML={{ __html: hint }}
    />
  );
};

const getWidthClass = (field, embedded = false) => {
  if (embedded) return "";
  return parseBooleanVal(field?.isFullWidth)
    ? "col-span-12"
    : "col-span-12 md:col-span-6";
};

const CanvasShell = ({ field, embedded = false, children, className = "" }) => (
  <div
    className={`${getWidthClass(field, embedded)} flex min-w-0 w-full max-w-full flex-col did-floating-label-content form-builder-canvas-field ${className}`.trim()}
  >
    {children}
  </div>
);

const TextLikeInput = ({ field, embedded = false, type = "text", placeholder }) => (
  <CanvasShell field={field} embedded={embedded}>
    <label className="did-floating-label text-sm">
      {formatFieldLabel(field.lable)}
      {parseBooleanVal(field.required) && (
        <span className="form-builder-required-mark"> *</span>
      )}
    </label>
    <input
      className="did-floating-input"
      type={type}
      disabled
      readOnly
      placeholder={placeholder}
      value={field.value ?? ""}
    />
    <FieldHint hint={field.hint} />
  </CanvasShell>
);

const FormBuilderFieldCanvas = ({ field, embedded = true, builderMode = false }) => {
  if (!field) return null;

  const compactClass = builderMode ? "form-builder-canvas-compact" : "";

  switch (field.type) {
    case "textarea":
      return (
        <CanvasShell field={field} embedded={embedded} className={compactClass}>
          <label className="did-floating-label text-sm">
            {formatFieldLabel(field.lable)}
            {parseBooleanVal(field.required) && (
              <span className="form-builder-required-mark"> *</span>
            )}
          </label>
          <textarea
            className="form-textarea"
            rows={builderMode ? 2 : 4}
            disabled
            readOnly
            placeholder="Enter long text..."
            value={field.value ?? ""}
          />
          <FieldHint hint={field.hint} />
        </CanvasShell>
      );

    case "number":
      return (
        <TextLikeInput
          field={field}
          embedded={embedded}
          type="number"
          placeholder="0"
        />
      );

    case "date":
      return (
        <TextLikeInput field={field} embedded={embedded} type="date" placeholder="" />
      );

    case "email":
      return (
        <TextLikeInput
          field={field}
          embedded={embedded}
          type="email"
          placeholder="name@example.com"
        />
      );

    case "phone":
      return (
        <TextLikeInput
          field={field}
          embedded={embedded}
          type="tel"
          placeholder="04xx xxx xxx"
        />
      );

    case "select":
      return (
        <CanvasShell field={field} embedded={embedded}>
          <label className="did-floating-label text-sm">
            {formatFieldLabel(field.lable)}
            {parseBooleanVal(field.required) && (
              <span className="form-builder-required-mark"> *</span>
            )}
          </label>
          <select className="did-floating-select" disabled defaultValue="">
            <option value="" disabled>
              Select an option
            </option>
            {(field.options ?? []).map((option, index) => (
              <option key={`${option.lable}-${index}`} value={option.lable}>
                {option.lable}
              </option>
            ))}
          </select>
          <FieldHint hint={field.hint} />
        </CanvasShell>
      );

    case "radio":
      return (
        <CanvasShell field={field} embedded={embedded}>
          <label className="did-floating-label text-sm">
            {formatFieldLabel(field.lable)}
            {parseBooleanVal(field.required) && (
              <span className="form-builder-required-mark"> *</span>
            )}
          </label>
          <div className="form-radio-pills">
            {(field.options ?? [{ lable: "Option 1" }, { lable: "Option 2" }]).map(
              (option, index) => (
                <div key={`${option.lable}-${index}`} className="form-radio-pill">
                  <input type="radio" disabled id={`preview-radio-${index}`} />
                  <label htmlFor={`preview-radio-${index}`}>{option.lable}</label>
                </div>
              )
            )}
          </div>
          <FieldHint hint={field.hint} />
        </CanvasShell>
      );

    case "checkbox": {
      const checkboxId = `preview-checkbox-${field.name || formatFieldLabel(field.lable)}`;
      return (
        <div className={`${getWidthClass(field, embedded)} form-builder-canvas-field`.trim()}>
          <div className="form-checkbox-card">
            <input type="checkbox" disabled id={checkboxId} />
            <label htmlFor={checkboxId}>
              {formatFieldLabel(field.lable)}
            </label>
          </div>
          <FieldHint hint={field.hint} />
        </div>
      );
    }

    case "address":
      return (
        <div
          className={`${getWidthClass(field, embedded)} form-builder-canvas-field min-w-0 w-full max-w-full`.trim()}
        >
          <label className="did-floating-label text-sm mb-1 block w-full max-w-full">
            {formatFieldLabel(field.lable || "Address")}
          </label>
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-4 md:col-span-2 did-floating-label-content">
              <label className="did-floating-label text-sm">Street Name</label>
              <input
                className="did-floating-input"
                disabled
                readOnly
                value={field.address?.street ?? ""}
                placeholder="Street"
              />
            </div>
            <div className="col-span-2 md:col-span-1 did-floating-label-content">
              <label className="did-floating-label text-sm">Suburb</label>
              <input
                className="did-floating-input"
                disabled
                readOnly
                value={field.address?.subrub ?? ""}
                placeholder="Suburb"
              />
            </div>
            <div className="col-span-2 md:col-span-1 did-floating-label-content">
              <label className="did-floating-label text-sm">Postcode</label>
              <input
                className="did-floating-input"
                disabled
                readOnly
                value={field.address?.postcode ?? ""}
                placeholder="0000"
              />
            </div>
            <div className="col-span-4 md:col-span-2 did-floating-label-content">
              <label className="did-floating-label text-sm">State</label>
              <select className="did-floating-select" disabled defaultValue="">
                <option value="">
                  {field.address?.state ||
                    STATE_FULL_LABEL_OPTIONS?.[0]?.label ||
                    "Select state"}
                </option>
              </select>
            </div>
          </div>
          <FieldHint hint={field.hint} />
        </div>
      );

    case "file":
      return (
        <CanvasShell field={field} embedded={embedded}>
          <label className="did-floating-label text-sm">
            {formatFieldLabel(field.lable)}
            {parseBooleanVal(field.required) && (
              <span className="form-builder-required-mark"> *</span>
            )}
          </label>
          <div className="did-floating-input form-builder-file-input-preview">
            <span className="form-builder-upload-chip">Click to upload file</span>
            <span className="form-builder-upload-placeholder">
              {field.value || "No file selected"}
            </span>
          </div>
          <FieldHint hint={field.hint} />
        </CanvasShell>
      );

    case "signature":
      return (
        <CanvasShell field={field} embedded={embedded}>
          <label className="did-floating-label text-sm">
            {formatFieldLabel(field.name || field.lable || "Signature")}
          </label>
          <div className="form-signature-display-inner">
            <div className="form-signature-empty">Sign here</div>
          </div>
          <FieldHint hint={field.hint} />
        </CanvasShell>
      );

    case "table":
      return (
        <div
          className={`${
            embedded ? "" : "col-span-12"
          } form-builder-canvas-field`.trim()}
        >
          <FormReadOnlyTable
            label={field.lable}
            columns={field.columns ?? []}
            rows={field.rows ?? []}
            hint={field.hint}
          />
        </div>
      );

    case "content":
      return (
        <div
          className={`${
            embedded ? "" : "col-span-12"
          } form-builder-canvas-field`.trim()}
        >
          <div
            className="form-content-block"
            dangerouslySetInnerHTML={{
              __html: field.content || "<p>Content block</p>",
            }}
          />
          <FieldHint hint={field.hint} />
        </div>
      );

    case "sub_section":
      return (
        <div className="col-span-12 form-builder-canvas-subsection-placeholder">
          <span className="form-subsection-title">
            {formatFieldLabel(field.section_title || "Sub-section")}
          </span>
          <span className="form-builder-canvas-subsection-meta">
            {(field.sub_fields ?? []).length} nested field
            {(field.sub_fields ?? []).length === 1 ? "" : "s"}
          </span>
        </div>
      );

    default:
      return (
        <TextLikeInput
          field={field}
          embedded={embedded}
          type="text"
          placeholder="Enter text..."
        />
      );
  }
};

export default FormBuilderFieldCanvas;
